// Libraries
import _ from 'lodash';

// Supermove
import {gql} from '@supermove/graphql';
import {UserModel} from '@supermove/models';
import {Json, Phone, withFragment} from '@supermove/utils';

// App
import UserRole from '@shared/modules/User/enums/UserRole';
import UserStatus from '@shared/modules/User/enums/UserStatus';

const canViewCustomerInfo = ({role, features}: any) => {
  const jsonFeatures = Json.parse(features);
  return (
    !!_.get(jsonFeatures, 'VIEW_CUSTOMER_INFO') || UserRole.MANAGER_ROLES_PLUS_SUPER.includes(role)
  );
};

canViewCustomerInfo.fragment = gql`
  fragment User_canViewCustomerInfo on User {
    role
    features
  }
`;

const getFullName = (user: UserModel) => {
  return user.firstName;
};

const getSortedUserMoverPositions = withFragment(
  ({userMoverPositions}) => {
    return userMoverPositions.sort((a: any, b: any) => {
      // Put primary position first
      if (a.isPrimary) {
        return -1;
      }
      if (b.isPrimary) {
        return 1;
      }

      // Sort remaining positions alphabetically by name
      return a.moverPosition.name.localeCompare(b.moverPosition.name);
    });
  },
  gql`
    fragment User_getSortedUserMoverPositions on User {
      id
      userMoverPositions {
        id
        isPrimary
        moverPosition {
          id
          name
        }
      }
    }
  `,
);

export const getOfficeUsersByStatus = withFragment(
  (officeUsers: UserModel[]) => {
    return officeUsers.reduce(
      (result, user: UserModel) => {
        if (user.status === UserStatus.ACTIVE) {
          result.activeUsers.push(user);
        } else if (user.status === UserStatus.PENDING) {
          result.pendingUsers.push(user);
        } else if (user.status === UserStatus.INACTIVE) {
          result.inactiveUsers.push(user);
        }
        return result;
      },
      {
        activeUsers: [] as UserModel[],
        pendingUsers: [] as UserModel[],
        inactiveUsers: [] as UserModel[],
      },
    );
  },
  gql`
    fragment User_getOfficeUsersByStatus on User {
      id
      status
    }
  `,
);

const User = {
  canViewCustomerInfo,

  filterByIds: ({users, ids}: {users: UserModel[]; ids: string[]}) => {
    return users.filter((user: any) => ids.includes(user.id));
  },

  getContactInfo: withFragment(
    ({aliasName, fullName, phoneNumber, email}: UserModel) => {
      return [aliasName, fullName, Phone.display(phoneNumber), email].filter(Boolean).join('\n');
    },
    gql`
      fragment User_getContactInfo on User {
        id
        aliasName
        fullName
        phoneNumber
        email
      }
    `,
  ),

  getFullName,

  getInitials: (user: UserModel) => {
    // Since firstName is now the full name, we need to split by space and join.
    const names = user.firstName.split(' ');
    const firstLetters = names.map((name: any) => _.startCase(_.first(name)));
    return firstLetters.join('');
  },

  getPosition: (user: UserModel) => {
    return _.startCase(_.toLower(user.position));
  },
  getRole: (user: UserModel) => {
    return _.startCase(_.toLower(user.role));
  },
  getCustomerDisplayName: (user: UserModel) => {
    // Returns customer's alias name if one exists,
    // otherwise returns first and last name.
    return user.aliasName || getFullName(user);
  },

  getSortedUserMoverPositions,
  getOfficeUsersByStatus,
};

export default User;
